import { ComboBoxContextProvider, ComboBoxContextProviderProps } from './contexts/comboBoxContext'
import { ComboBoxDropdown, ComboBoxDropdownProps } from './elements/ComboBoxDropdown'
import { ComboBoxInput, ComboBoxInputProps } from './elements/ComboBoxInput'
import { ComboBoxSelectedItems, ComboBoxSelectedItemsProps } from './elements/ComboBoxSelectedItems'
import { ComboBoxValue } from './types/comboBoxValue'

export type ComboBoxProps<T> = Omit<ComboBoxDropdownProps, 'trigger'> &
  Omit<ComboBoxInputProps, 'onChange' | 'inChangeDebounced'> &
  Partial<ComboBoxSelectedItemsProps> &
  ComboBoxContextProviderProps<T> & {
    onInputBlur?: ComboBoxInputProps['onBlur']
    onInputChange?: ComboBoxInputProps['onChange']
    onInputChangeDebounced?: ComboBoxInputProps['onChangeDebounced']
  }

export const ComboBox = <T,>({
  // Context props:
  formatDisplayedValue,
  initialInputValue,
  isMultiselect = true,
  items,
  onChange,
  selectedValues,
  validateItem,

  // Selected items props:
  selectedItemsPrefix,

  // Dropdown props
  addNewItemButtonText,
  dropdownId,
  dropdownMaxHeight = 'default',
  dropdownSize = 'fitTrigger',
  footer,
  placement = 'bottom-end',
  shouldDisplayItemValue = true,

  // Input props:
  alignment = 'left',
  autoCompleted,
  autoFocus,
  bordered,
  className,
  disabled,
  error,
  focused = false,
  hidden,
  id,
  maxItems,
  name,
  onInputBlur,
  onInputChange,
  onInputChangeDebounced,
  onFocus,
  onKeyPress,
  onMouseDown,
  onPressEnter,
  placeholder,
  readOnly,
  selectOnFocus,
  size,
  success,
  type,
}: ComboBoxProps<T>) => {
  const commonContextProps = {
    formatDisplayedValue,
    initialInputValue,
    items,
    maxItems,
    selectedValues,
    validateItem,
  }

  const contextProps = isMultiselect
    ? {
        ...commonContextProps,
        isMultiselect: true as const,
        onChange: onChange as (values: ComboBoxValue[]) => void,
      }
    : {
        ...commonContextProps,
        isMultiselect: false as const,
        onChange: onChange as (value: ComboBoxValue) => void,
      }

  const selectedItemsProps = {
    selectedItemsPrefix,
  }

  const dropdownProps = {
    addNewItemButtonText,
    dropdownId,
    dropdownMaxHeight,
    dropdownSize,
    footer,
    placement,
    shouldDisplayItemValue,
  }

  const inputProps = {
    alignment,
    autoCompleted,
    autoFocus,
    bordered,
    className,
    disabled,
    error,
    focused,
    hidden,
    id,
    name,
    onBlur: onInputBlur,
    onChange: onInputChange,
    onChangeDebounced: onInputChangeDebounced,
    onFocus,
    onKeyPress,
    onMouseDown,
    onPressEnter,
    placeholder,
    readOnly,
    selectOnFocus,
    size,
    success,
    type,
  }

  return (
    <ComboBoxContextProvider<T> {...contextProps}>
      {isMultiselect && <ComboBoxSelectedItems {...selectedItemsProps} />}
      <ComboBoxDropdown {...dropdownProps} trigger={<ComboBoxInput {...inputProps} />} />
    </ComboBoxContextProvider>
  )
}
